// Directory-aware traversal for drag-and-drop uploads.
//
// A folder dragged onto the window arrives as `DataTransferItem`s. Reading
// `dataTransfer.files` FLATTENS the folder away (you get the files with no
// paths, or nothing at all for a directory). To preserve the hierarchy we walk
// each item's `webkitGetAsEntry()` tree recursively and emit `{file, relPath}`
// pairs — the exact shape the folder *picker* path already produces from each
// File's `webkitRelativePath`. The backend then mirrors `relPath` under
// `Papers/<subdir>/` and tags the document with `meta["folder"]`.

/** One file to upload, tagged with its path relative to the dropped folder. */
export interface UploadItem {
  file: File;
  /** e.g. "Research/2024/paper.pdf". Undefined for a loose top-level file
   * (mirrors the picker's empty `webkitRelativePath`), so it lands flat. */
  relPath?: string;
}

// Minimal structural mirrors of the WebKit FileSystem Entry API. Structural
// (not `FileSystemEntry`) so the traversal is unit-testable with plain mocks;
// real browsers pass the genuine DOM objects, which satisfy these shapes.
export interface FileSystemEntryLike {
  readonly isFile: boolean;
  readonly isDirectory: boolean;
  readonly name: string;
  file?(
    successCallback: (file: File) => void,
    errorCallback?: (err: unknown) => void,
  ): void;
  createReader?(): DirectoryReaderLike;
}

export interface DirectoryReaderLike {
  readEntries(
    successCallback: (entries: FileSystemEntryLike[]) => void,
    errorCallback?: (err: unknown) => void,
  ): void;
}

// Safety bounds on a single drop. Generous enough for a real research folder,
// but they cap a pathological drag (e.g. a home directory) so the browser isn't
// asked to open tens of thousands of files or recurse without end. Each file
// becomes its own upload + ingest job, so the count is the meaningful limit;
// depth guards against symlink-ish loops and absurd nesting.
export const MAX_UPLOAD_FILES = 1000;
export const MAX_UPLOAD_DEPTH = 20;

export interface CollectResult {
  items: UploadItem[];
  /** Hit the file-count cap — some files were left out. */
  truncated: boolean;
  /** Pruned a subtree past the depth cap — some deep folders were skipped. */
  depthTruncated: boolean;
}

/** Deterministic order so a folder always ingests the same way. Name-ordered,
 * locale-independent (portable across machines/CI). */
function sortEntries(entries: FileSystemEntryLike[]): FileSystemEntryLike[] {
  return [...entries].sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
}

function readFile(entry: FileSystemEntryLike): Promise<File | null> {
  return new Promise((resolve) => {
    if (!entry.file) {
      resolve(null);
      return;
    }
    entry.file(
      (f) => resolve(f),
      () => resolve(null),
    );
  });
}

/** `readEntries` yields a directory's children in BATCHES; it must be called
 * until it returns an empty list, or large folders come back partial. */
async function readAllEntries(entry: FileSystemEntryLike): Promise<FileSystemEntryLike[]> {
  if (!entry.createReader) return [];
  const reader = entry.createReader();
  const all: FileSystemEntryLike[] = [];
  for (;;) {
    const batch = await new Promise<FileSystemEntryLike[]>((resolve) => {
      reader.readEntries(
        (entries) => resolve(entries),
        () => resolve([]),
      );
    });
    if (!batch.length) break;
    all.push(...batch);
  }
  return all;
}

/** Walk dropped filesystem entries into `{file, relPath}` pairs, preserving the
 * folder hierarchy and bounding depth + file count. */
export async function collectEntries(
  entries: FileSystemEntryLike[],
  limits: { maxFiles?: number; maxDepth?: number } = {},
): Promise<CollectResult> {
  const maxFiles = limits.maxFiles ?? MAX_UPLOAD_FILES;
  const maxDepth = limits.maxDepth ?? MAX_UPLOAD_DEPTH;
  const items: UploadItem[] = [];
  let truncated = false;
  let depthTruncated = false;

  const walk = async (
    entry: FileSystemEntryLike,
    prefix: string,
    depth: number,
  ): Promise<void> => {
    if (items.length >= maxFiles) {
      truncated = true;
      return;
    }
    if (entry.isFile) {
      const file = await readFile(entry);
      if (file) items.push({ file, relPath: prefix ? `${prefix}/${entry.name}` : undefined });
      return;
    }
    if (entry.isDirectory) {
      if (depth >= maxDepth) {
        depthTruncated = true;
        return;
      }
      const dirPath = prefix ? `${prefix}/${entry.name}` : entry.name;
      for (const child of sortEntries(await readAllEntries(entry))) {
        if (items.length >= maxFiles) {
          truncated = true;
          break;
        }
        await walk(child, dirPath, depth + 1);
      }
    }
  };

  for (const entry of sortEntries(entries)) {
    if (items.length >= maxFiles) {
      truncated = true;
      break;
    }
    await walk(entry, "", 0);
  }
  return { items, truncated, depthTruncated };
}
